/**
 * ForderungenSeite — native Forderungs-Seite pro Firma (Finanzen)
 *
 * Ansichten: Fälle, Planer, Email-Ausgang, Email-Eingang, Versicherungen, Vorlagen.
 * Datenquelle: v_forderungs_eskalation, v_forderungs_versandplan, v_forderungs_abgleich,
 * v_forderungs_pipeline, v_forderungs_versicherungs_statistik, v_forderungs_kuerzungen,
 * forderungs_eskalation_log, forderungs_vorlagen, forderung_notiz, email_drafts,
 * eingang_anwaltspost, bank_stand.
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Scale, Euro, AlertTriangle, Search, RotateCcw, Clock, ShieldCheck,
  FileWarning, Mailbox, Send, Inbox, FileText, Save, CheckCircle2, Loader2, CalendarDays, Eye,
  ChevronDown, MessageSquare, AlertCircle, BarChart3, Scissors,
} from 'lucide-react';

type Fall = Record<string, any>;
type Firma = 'sv' | 'lcs' | 'alle';
type Ansicht = 'faelle' | 'planer' | 'vorlagen' | 'ausgang' | 'eingang' | 'statistik';

const VERSAND_KEY = 'koop-versand-7Xq2';
const TEST_EMPFAENGER = 'info@gutachter-koop.de';
const STAPEL = 25;

const KATEGORIE_LABEL: Record<string, string> = {
  voll_offen: 'Voll offen', teilzahlung_kuerzung: 'Teilzahlung / Kürzung',
  verjaehrt: 'Verjährt', moeglich_bezahlt: 'Möglicherweise bezahlt',
  gehemmt_klage: 'Gehemmt (Klage)', gehemmt: 'Gehemmt',
};
const KATEGORIE_FARBE: Record<string, string> = {
  voll_offen: 'bg-blue-100 text-blue-700', teilzahlung_kuerzung: 'bg-amber-100 text-amber-800',
  verjaehrt: 'bg-red-100 text-red-700', moeglich_bezahlt: 'bg-zinc-100 text-zinc-500',
  gehemmt_klage: 'bg-violet-100 text-violet-700', gehemmt: 'bg-violet-100 text-violet-700',
};
const STUFE_LABEL: Record<number, string> = {
  0: 'Sachstand', 1: 'Zahlungserinnerung', 2: '1. Mahnung', 3: '2. Mahnung',
  4: 'Letzte Mahnung', 5: 'Mahnung vor Übergabe', 6: 'Übergabe Kanzlei', 9: 'Verjährt-Anfrage',
};
const STUFE_FARBE: Record<number, string> = {
  0: '#e5e7eb', 1: '#cfe8ff', 2: '#9ecbff', 3: '#ffe08a', 4: '#ffb866',
  5: '#ff7a59', 6: '#e03131', 9: '#b39ddb',
};
const DRAFT_STATUS_LABEL: Record<string, string> = {
  draft: 'Zurückgehalten', ready_for_review: 'Freigegeben', sent: 'Versendet', undone: 'Storniert',
};
const AGENT_LABEL: Record<string, string> = {
  'forderungs-eskalation': 'Mahnung', 'verjaehrt-anfrage': 'Verjährt-Anfrage', 'anwalt-infoschreiben': 'Anwalt-Info',
};
const KAT_FEIN_FARBE: Record<string, string> = {
  zahlung: 'bg-emerald-100 text-emerald-700', kuerzung: 'bg-amber-100 text-amber-800',
  haftung: 'bg-orange-100 text-orange-700', sachstand: 'bg-blue-100 text-blue-700',
  stellungnahme: 'bg-violet-100 text-violet-700', nachbesichtigung: 'bg-sky-100 text-sky-700',
  sonstiges: 'bg-zinc-100 text-zinc-500',
};

function eur(n: number | null | undefined): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n || 0);
}
function datum(d: string | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('de-DE');
}
function datumZeit(d: string | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function tageSeit(d: string | null | undefined): number | null {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : Math.floor((Date.now() - dt.getTime()) / 86_400_000);
}
function pad(n: number): string { return String(n).padStart(2, '0'); }

// ═══════════════════════════════════════════════════════════════════════════
export default function ForderungenSeite({ firma: firmaProp, embedded = false }: { firma?: Firma; embedded?: boolean } = {}) {
  const [firmaState, setFirmaState] = useState<Firma>(firmaProp ?? 'sv');
  const firma: Firma = embedded ? (firmaProp ?? 'sv') : firmaState;
  const [ansicht, setAnsicht] = useState<Ansicht>('faelle');
  const [faelle, setFaelle] = useState<Fall[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bankStand, setBankStand] = useState<Fall | null>(null);
  const [reload, setReload] = useState(0);

  const [suche, setSuche] = useState('');
  const [kategorieFilter, setKategorieFilter] = useState('alle');
  const [stufeFilter, setStufeFilter] = useState('alle');
  const [nurUnvollstaendig, setNurUnvollstaendig] = useState(false);
  const [sortKey, setSortKey] = useState<'offen' | 'verjaehrung' | 'stufe' | 'verzug'>('offen');
  const [aktivAk, setAktivAk] = useState<string | null>(null);

  useEffect(() => {
    let abbruch = false;
    setLoading(true); setFehler(null);
    (async () => {
      const { data, error } = await supabase.from('v_forderungs_eskalation').select('*').limit(5000);
      if (abbruch) return;
      if (error) { setFehler(error.message); setFaelle([]); } else { setFaelle(data ?? []); }
      const bank = await supabase.from('bank_stand').select('*').order('stand_datum', { ascending: false });
      if (!abbruch && !bank.error) setBankStand((bank.data ?? [])[0] ?? null);
      if (!abbruch) setLoading(false);
    })();
    return () => { abbruch = true; };
  }, [reload]);

  const proAk = useMemo(() => {
    const map = new Map<string, Fall>();
    for (const r of faelle) {
      const ak = r.ak ?? r.akte_id ?? '';
      if (!ak) continue;
      const v = map.get(ak);
      if (!v) map.set(ak, { ...r, offen: Number(r.offen) || 0, _n: 1 });
      else { v.offen += Number(r.offen) || 0; v.verzugszins_eur = (Number(v.verzugszins_eur) || 0) + (Number(r.verzugszins_eur) || 0); v._n += 1; }
    }
    return Array.from(map.values());
  }, [faelle]);

  const kpis = useMemo(() => {
    const k: Record<string, { anzahl: number; offen: number }> = {}; let gesamt = 0;
    for (const r of proAk) {
      const kat = r.fall_kategorie ?? 'voll_offen';
      k[kat] = k[kat] || { anzahl: 0, offen: 0 };
      k[kat].anzahl += 1; k[kat].offen += r.offen; gesamt += r.offen;
    }
    return { k, gesamt, anzahl: proAk.length };
  }, [proAk]);

  const gefiltert = useMemo(() => {
    let list = proAk;
    if (kategorieFilter !== 'alle') list = list.filter((r) => (r.fall_kategorie ?? 'voll_offen') === kategorieFilter);
    if (stufeFilter !== 'alle') list = list.filter((r) => String(r.stufe ?? '') === stufeFilter);
    if (nurUnvollstaendig) list = list.filter((r) => !r.unterlagen_vollstaendig);
    if (suche.trim()) {
      const q = suche.trim().toLowerCase();
      list = list.filter((r) => [r.ak, r.geschaedigter, r.gegner_name, r.versicherung, r.kanzlei, r.schadennummer, r.versicherungsnummer]
        .filter(Boolean).some((v: any) => String(v).toLowerCase().includes(q)));
    }
    return [...list].sort((a, b) => {
      if (sortKey === 'offen') return b.offen - a.offen;
      if (sortKey === 'verzug') return (Number(b.verzugszins_eur) || 0) - (Number(a.verzugszins_eur) || 0);
      if (sortKey === 'stufe') return (Number(b.stufe) || 0) - (Number(a.stufe) || 0);
      if (sortKey === 'verjaehrung') return (Number(a.verjaehrung_jahr) || 9999) - (Number(b.verjaehrung_jahr) || 9999);
      return 0;
    });
  }, [proAk, kategorieFilter, stufeFilter, nurUnvollstaendig, suche, sortKey]);

  const resetFilter = useCallback(() => { setSuche(''); setKategorieFilter('alle'); setStufeFilter('alle'); setNurUnvollstaendig(false); }, []);
  const aktiverFall = useMemo(() => (aktivAk ? proAk.find((r) => (r.ak ?? r.akte_id) === aktivAk) ?? null : null), [aktivAk, proAk]);
  const bankAlter = tageSeit(bankStand?.stand_datum);

  return (
    <div className={embedded ? 'space-y-4' : 'p-6 max-w-[1400px] mx-auto space-y-4'}>
      {!embedded && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-2xl font-bold flex items-center gap-2"><Scale className="w-6 h-6 text-amber-600" />Forderungen</h1>
          <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1">
            {(['sv', 'lcs'] as Firma[]).map((f) => (
              <button key={f} type="button" onClick={() => setFirmaState(f)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${firma === f ? 'bg-white shadow text-amber-700' : 'text-muted-foreground hover:text-foreground'}`}>
                {f === 'sv' ? 'SV Büro (Gutachter Koop)' : 'LCS (Autoglas + Mietwagen)'}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 text-sm flex-wrap">
        {([['faelle', 'Fälle', Scale], ['planer', 'Planer', CalendarDays], ['ausgang', 'Email-Ausgang', Send], ['eingang', 'Email-Eingang', Inbox], ['statistik', 'Versicherungen', BarChart3], ['vorlagen', 'Vorlagen', FileText]] as const).map(([id, label, Icon]) => (
          <button key={id} type="button" onClick={() => setAnsicht(id as Ansicht)}
            className={`px-3 py-1.5 rounded-md border flex items-center gap-1.5 ${ansicht === id ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white text-muted-foreground'}`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {bankStand && (ansicht === 'faelle' || ansicht === 'planer') && firma !== 'lcs' && (
        <div className={`flex items-center gap-2 text-xs rounded-lg border px-3 py-2 ${bankAlter !== null && bankAlter > 4 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
          <Mailbox className="w-3.5 h-3.5" />Bankstand {bankStand.konto ?? ''}: {datum(bankStand.stand_datum)}
          {bankAlter !== null && ` (vor ${bankAlter} Tagen)`}
          {bankAlter !== null && bankAlter > 4 && ' — bitte neuen Bankumsatz hochladen.'}
        </div>
      )}

      {firma === 'lcs' && ansicht !== 'ausgang' && ansicht !== 'eingang' && ansicht !== 'statistik' ? (
        <div className="border rounded-lg p-12 text-center text-muted-foreground">
          <FileWarning className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">LCS-Forderungen sind in Vorbereitung.</p>
          <p className="text-sm mt-1">Sobald Autoglas-/Mietwagen-Rechnungen den Versicherungs-Schlüssel tragen, erscheinen sie hier.</p>
        </div>
      ) : ansicht === 'vorlagen' ? (
        <VorlagenEditor />
      ) : ansicht === 'planer' ? (
        <VersandPlaner />
      ) : ansicht === 'ausgang' ? (
        <EmailAusgang />
      ) : ansicht === 'eingang' ? (
        <EmailEingang />
      ) : ansicht === 'statistik' ? (
        <VersicherungsStatistik />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard icon={<Euro className="w-3.5 h-3.5" />} label="Offen gesamt" wert={eur(kpis.gesamt)} sub={`${kpis.anzahl} Fälle`} />
            <KpiCard icon={<ShieldCheck className="w-3.5 h-3.5 text-amber-600" />} label="Teilzahlung / Kürzung" wert={eur(kpis.k.teilzahlung_kuerzung?.offen ?? 0)} sub={`${kpis.k.teilzahlung_kuerzung?.anzahl ?? 0} Fälle`} />
            <KpiCard icon={<Clock className="w-3.5 h-3.5 text-blue-600" />} label="Voll offen" wert={eur(kpis.k.voll_offen?.offen ?? 0)} sub={`${kpis.k.voll_offen?.anzahl ?? 0} Fälle`} />
            <KpiCard icon={<AlertTriangle className="w-3.5 h-3.5 text-red-600" />} label="Verjährt (unsicher)" wert={eur(kpis.k.verjaehrt?.offen ?? 0)} sub={`${kpis.k.verjaehrt?.anzahl ?? 0} Fälle`} />
            <KpiCard icon={<Mailbox className="w-3.5 h-3.5 text-zinc-500" />} label="Mögl. bezahlt" wert={eur(kpis.k.moeglich_bezahlt?.offen ?? 0)} sub={`${kpis.k.moeglich_bezahlt?.anzahl ?? 0} Fälle`} />
          </div>

          <VersandLeiste onFertig={() => setReload((n) => n + 1)} />

          <div className="flex flex-wrap items-center gap-2 border rounded-lg p-3 bg-muted/20">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
              <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Suche (AK, Mandant, Versicherung, Kanzlei …)" className="pl-7 pr-2 py-1.5 text-sm border rounded-md w-72 bg-white" />
            </div>
            <select value={kategorieFilter} onChange={(e) => setKategorieFilter(e.target.value)} className="text-sm border rounded-md px-2 py-1.5 bg-white">
              <option value="alle">Alle Kategorien</option>
              {Object.entries(KATEGORIE_LABEL).map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
            </select>
            <select value={stufeFilter} onChange={(e) => setStufeFilter(e.target.value)} className="text-sm border rounded-md px-2 py-1.5 bg-white">
              <option value="alle">Alle Stufen</option>
              {[0, 1, 2, 3, 4, 5, 6].map((s) => (<option key={s} value={s}>{s}: {STUFE_LABEL[s]}</option>))}
            </select>
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <input type="checkbox" checked={nurUnvollstaendig} onChange={(e) => setNurUnvollstaendig(e.target.checked)} />nur unvollständige Unterlagen
            </label>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value as any)} className="text-sm border rounded-md px-2 py-1.5 bg-white ml-auto">
              <option value="offen">Sortierung: Offen ↓</option>
              <option value="verzug">Verzugszins ↓</option>
              <option value="stufe">Stufe ↓</option>
              <option value="verjaehrung">Verjährung ↑</option>
            </select>
            <button type="button" onClick={resetFilter} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border rounded-md px-2 py-1.5"><RotateCcw className="w-3 h-3" />Reset</button>
          </div>

          {fehler && (<div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">Daten konnten nicht geladen werden: {fehler}</div>)}

          <div className="flex gap-4 items-start">
            <div className="flex-1 border rounded-lg overflow-hidden">
              <div className="px-3 py-2 text-xs text-muted-foreground border-b bg-muted/30">{loading ? 'Lade…' : `${gefiltert.length} Fälle`}</div>
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white border-b text-xs text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">AK / Mandant ./. VN</th>
                      <th className="text-left font-medium px-3 py-2">Versicherung / Kanzlei</th>
                      <th className="text-right font-medium px-3 py-2">Offen</th>
                      <th className="text-right font-medium px-3 py-2">Verzugszins</th>
                      <th className="text-center font-medium px-3 py-2">Stufe</th>
                      <th className="text-center font-medium px-3 py-2">Verj.</th>
                      <th className="text-center font-medium px-3 py-2">Unterl.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gefiltert.map((r) => {
                      const ak = r.ak ?? r.akte_id; const aktiv = aktivAk === ak; const kat = r.fall_kategorie ?? 'voll_offen';
                      return (
                        <tr key={ak} onClick={() => setAktivAk(ak)} className={`border-b cursor-pointer transition-colors ${aktiv ? 'bg-amber-50' : 'hover:bg-muted/30'}`}>
                          <td className="px-3 py-2">
                            <div className="font-mono text-xs font-semibold">{ak}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[180px]">{r.geschaedigter ?? '—'}{r.gegner_name ? ` ./. ${r.gegner_name}` : ''}</div>
                            <span className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${KATEGORIE_FARBE[kat] ?? 'bg-gray-100 text-gray-600'}`}>{KATEGORIE_LABEL[kat] ?? kat}</span>
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <div className="truncate max-w-[200px]">{r.versicherung ?? '—'}</div>
                            <div className="text-muted-foreground truncate max-w-[200px]">{r.kanzlei ?? '—'}</div>
                          </td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums">{eur(r.offen)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{eur(r.verzugszins_eur)}</td>
                          <td className="px-3 py-2 text-center"><span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted">{r.stufe ?? '—'}</span></td>
                          <td className="px-3 py-2 text-center text-xs"><span className={Number(r.verjaehrung_jahr) <= 2026 ? 'text-red-600 font-semibold' : ''}>{r.verjaehrung_jahr ?? '—'}</span></td>
                          <td className="px-3 py-2 text-center"><span className={`inline-block w-2.5 h-2.5 rounded-full ${r.unterlagen_vollstaendig ? 'bg-emerald-500' : 'bg-red-400'}`} title={r.unterlagen_vollstaendig ? 'vollständig' : 'unvollständig'} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            {aktiverFall && (<FallDetail fall={aktiverFall} onClose={() => setAktivAk(null)} />)}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Versandleiste ──────────────────────────────────────────────────────────
function VersandLeiste({ onFertig }: { onFertig: () => void }) {
  const [busy, setBusy] = useState<'test' | 'scharf' | null>(null);
  const [ergebnis, setErgebnis] = useState<string | null>(null);
  const senden = async (modus: 'test' | 'scharf') => {
    if (modus === 'scharf' && !window.confirm(`Die nächsten ${STAPEL} freigegebenen Schreiben SCHARF an die Versicherungen/Anwälte versenden?`)) return;
    setBusy(modus); setErgebnis(null);
    try {
      const params = new URLSearchParams({ key: VERSAND_KEY, limit: String(STAPEL) });
      if (modus === 'test') params.set('test_to', TEST_EMPFAENGER);
      const { data, error } = await supabase.functions.invoke(`forderungs-versand?${params.toString()}`, { method: 'POST' });
      if (error) setErgebnis(`Fehler: ${error.message}`);
      else { const g = (data && (data.gesendet ?? data.sent ?? data.count)) ?? 0; setErgebnis(modus === 'test' ? `Test gesendet an ${TEST_EMPFAENGER}: ${g} Schreiben.` : `Scharf versendet: ${g} Schreiben.`); onFertig(); }
    } catch (e: any) { setErgebnis(`Fehler: ${e?.message ?? String(e)}`); } finally { setBusy(null); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 border rounded-lg p-3 bg-amber-50/40">
      <span className="text-xs text-muted-foreground mr-1">Versand ({STAPEL}er-Stapel · max. 60/Tag · 1 pro Empfänger/Tag):</span>
      <button type="button" onClick={() => senden('test')} disabled={busy !== null} className="flex items-center gap-1.5 text-sm border rounded-md px-3 py-1.5 bg-white hover:bg-muted/40 disabled:opacity-50">
        {busy === 'test' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mailbox className="w-3.5 h-3.5" />}Test an info@
      </button>
      <button type="button" onClick={() => senden('scharf')} disabled={busy !== null} className="flex items-center gap-1.5 text-sm rounded-md px-3 py-1.5 bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
        {busy === 'scharf' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Nächste {STAPEL} scharf senden
      </button>
      {ergebnis && <span className="text-xs text-muted-foreground">{ergebnis}</span>}
    </div>
  );
}

function KpiCard({ icon, label, wert, sub }: { icon: React.ReactNode; label: string; wert: string; sub: string }) {
  return (
    <div className="border rounded-lg p-3 bg-white">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">{icon}{label}</div>
      <div className="text-base font-semibold tabular-nums">{wert}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}

// ─── Email-Ausgang ──────────────────────────────────────────────────────────
function EmailAusgang() {
  const [rows, setRows] = useState<Fall[]>([]);
  const [loading, setLoading] = useState(true);
  const [suche, setSuche] = useState('');
  const [st, setSt] = useState('alle');
  const [art, setArt] = useState('alle');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let abbruch = false;
    (async () => {
      const r = await supabase.from('email_drafts')
        .select('id,subject,recipient,cc,status,sent_at,created_at,created_by_agent_id,body_text')
        .in('created_by_agent_id', ['forderungs-eskalation', 'verjaehrt-anfrage', 'anwalt-infoschreiben'])
        .order('created_at', { ascending: false }).limit(4000);
      if (!abbruch) { setRows(r.error ? [] : (r.data ?? [])); setLoading(false); }
    })();
    return () => { abbruch = true; };
  }, []);

  const gef = useMemo(() => {
    let l = rows;
    if (st !== 'alle') l = l.filter((x) => x.status === st);
    if (art !== 'alle') l = l.filter((x) => x.created_by_agent_id === art);
    if (suche.trim()) { const q = suche.toLowerCase(); l = l.filter((x) => [x.subject, x.recipient].filter(Boolean).some((v: any) => String(v).toLowerCase().includes(q))); }
    return l;
  }, [rows, st, art, suche]);

  const zaehler = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach((x) => { c[x.status] = (c[x.status] || 0) + 1; });
    return c;
  }, [rows]);

  if (loading) return <div className="text-sm text-muted-foreground p-6">Lade ausgehende Mails…</div>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiCard icon={<Send className="w-3.5 h-3.5 text-emerald-600" />} label="Versendet" wert={String(zaehler['sent'] ?? 0)} sub="Mails raus" />
        <KpiCard icon={<CheckCircle2 className="w-3.5 h-3.5 text-amber-600" />} label="Freigegeben" wert={String(zaehler['ready_for_review'] ?? 0)} sub="wartet auf Versand" />
        <KpiCard icon={<Clock className="w-3.5 h-3.5 text-zinc-500" />} label="Zurückgehalten" wert={String(zaehler['draft'] ?? 0)} sub="Entwurf" />
        <KpiCard icon={<Mailbox className="w-3.5 h-3.5" />} label="Gesamt" wert={String(rows.length)} sub="alle Schreiben" />
      </div>
      <div className="flex flex-wrap items-center gap-2 border rounded-lg p-3 bg-muted/20">
        <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" /><input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Suche (Empfänger, Betreff)" className="pl-7 pr-2 py-1.5 text-sm border rounded-md w-64 bg-white" /></div>
        <select value={st} onChange={(e) => setSt(e.target.value)} className="text-sm border rounded-md px-2 py-1.5 bg-white">
          <option value="alle">Alle Status</option>
          {Object.entries(DRAFT_STATUS_LABEL).map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
        </select>
        <select value={art} onChange={(e) => setArt(e.target.value)} className="text-sm border rounded-md px-2 py-1.5 bg-white">
          <option value="alle">Alle Arten</option>
          {Object.entries(AGENT_LABEL).map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
        </select>
        <span className="text-xs text-muted-foreground ml-auto">{gef.length} Treffer</span>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <div className="max-h-[62vh] overflow-y-auto divide-y">
          {gef.map((m) => (
            <div key={m.id}>
              <button type="button" onClick={() => setOpen(open === m.id ? null : m.id)} className="w-full text-left px-3 py-2 hover:bg-muted/30 flex items-center gap-3">
                <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-muted-foreground transition-transform ${open === m.id ? 'rotate-180' : ''}`} />
                <span className="text-xs text-muted-foreground w-24 flex-shrink-0">{datumZeit(m.sent_at ?? m.created_at)}</span>
                <span className="text-sm truncate flex-1">{m.subject ?? '—'}</span>
                <span className="text-xs text-muted-foreground truncate w-48 hidden md:block">{m.recipient ?? '—'}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted flex-shrink-0">{AGENT_LABEL[m.created_by_agent_id] ?? m.created_by_agent_id}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${m.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : m.status === 'ready_for_review' ? 'bg-amber-100 text-amber-800' : 'bg-zinc-100 text-zinc-600'}`}>{DRAFT_STATUS_LABEL[m.status] ?? m.status}</span>
              </button>
              {open === m.id && (
                <div className="px-10 py-3 bg-muted/10 text-xs space-y-1">
                  <div><b>An:</b> {m.recipient ?? '—'}{m.cc && m.cc.length ? <> · <b>CC:</b> {Array.isArray(m.cc) ? m.cc.join(', ') : m.cc}</> : null}</div>
                  <pre className="whitespace-pre-wrap font-sans bg-white border rounded-md p-3 max-h-72 overflow-y-auto">{m.body_text ?? '(kein Text)'}</pre>
                </div>
              )}
            </div>
          ))}
          {!gef.length && <div className="px-3 py-10 text-center text-sm text-muted-foreground">Keine Mails.</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Email-Eingang (mit Relevanz-Filter + Klassifizierung) ──────────────────
function EmailEingang() {
  const [rows, setRows] = useState<Fall[]>([]);
  const [loading, setLoading] = useState(true);
  const [suche, setSuche] = useState('');
  const [kat, setKat] = useState('alle');
  const [nurRelevant, setNurRelevant] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let abbruch = false;
    (async () => {
      const r = await supabase.from('eingang_anwaltspost')
        .select('gmail_message_id,ak,from_email,from_name,subject,snippet,datum,kategorie_fein,forderungsrelevant,stellungnahme,nachbesichtigung,has_attachments,betrag_text')
        .order('datum', { ascending: false }).limit(5000);
      if (!abbruch) { setRows(r.error ? [] : (r.data ?? [])); setLoading(false); }
    })();
    return () => { abbruch = true; };
  }, []);

  const kategorien = useMemo(() => Array.from(new Set(rows.map((x) => x.kategorie_fein).filter(Boolean))), [rows]);
  const gef = useMemo(() => {
    let l = rows;
    if (nurRelevant) l = l.filter((x) => x.forderungsrelevant === true);
    if (kat !== 'alle') l = l.filter((x) => x.kategorie_fein === kat);
    if (suche.trim()) { const q = suche.toLowerCase(); l = l.filter((x) => [x.subject, x.from_email, x.from_name, x.ak, x.snippet].filter(Boolean).some((v: any) => String(v).toLowerCase().includes(q))); }
    return l;
  }, [rows, kat, suche, nurRelevant]);

  if (loading) return <div className="text-sm text-muted-foreground p-6">Lade eingehende Mails…</div>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiCard icon={<Inbox className="w-3.5 h-3.5" />} label="Eingegangen" wert={String(rows.length)} sub="Anwalts-/Versicherungspost" />
        <KpiCard icon={<ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />} label="Forderungsrelevant" wert={String(rows.filter((x) => x.forderungsrelevant).length)} sub="für Mahnwesen" />
        <KpiCard icon={<MessageSquare className="w-3.5 h-3.5 text-violet-600" />} label="Stellungnahme" wert={String(rows.filter((x) => x.stellungnahme).length)} sub="→ Gutachten" />
        <KpiCard icon={<Eye className="w-3.5 h-3.5 text-sky-600" />} label="Nachbesichtigung" wert={String(rows.filter((x) => x.nachbesichtigung).length)} sub="→ Gutachten" />
      </div>
      <div className="flex flex-wrap items-center gap-2 border rounded-lg p-3 bg-muted/20">
        <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" /><input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Suche (Absender, Betreff, AK)" className="pl-7 pr-2 py-1.5 text-sm border rounded-md w-64 bg-white" /></div>
        <select value={kat} onChange={(e) => setKat(e.target.value)} className="text-sm border rounded-md px-2 py-1.5 bg-white">
          <option value="alle">Alle Kategorien</option>
          {kategorien.map((k) => (<option key={k} value={k}>{k}</option>))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <input type="checkbox" checked={nurRelevant} onChange={(e) => setNurRelevant(e.target.checked)} />nur forderungsrelevant
        </label>
        <span className="text-xs text-muted-foreground ml-auto">{gef.length} Treffer</span>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <div className="max-h-[62vh] overflow-y-auto divide-y">
          {gef.map((m, i) => (
            <div key={m.gmail_message_id ?? i}>
              <button type="button" onClick={() => setOpen(open === (m.gmail_message_id ?? String(i)) ? null : (m.gmail_message_id ?? String(i)))} className="w-full text-left px-3 py-2 hover:bg-muted/30 flex items-center gap-3">
                <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-muted-foreground transition-transform ${open === (m.gmail_message_id ?? String(i)) ? 'rotate-180' : ''}`} />
                <span className="text-xs text-muted-foreground w-20 flex-shrink-0">{datum(m.datum)}</span>
                <span className="text-sm truncate flex-1">{m.subject ?? '—'}</span>
                <span className="text-xs text-muted-foreground truncate w-40 hidden md:block">{m.from_name || m.from_email || '—'}</span>
                {m.ak && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 flex-shrink-0">{m.ak}</span>}
                {m.kategorie_fein && <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${KAT_FEIN_FARBE[m.kategorie_fein] ?? 'bg-zinc-100 text-zinc-500'}`}>{m.kategorie_fein}</span>}
              </button>
              {open === (m.gmail_message_id ?? String(i)) && (
                <div className="px-10 py-3 bg-muted/10 text-xs space-y-1">
                  <div><b>Von:</b> {m.from_name ? `${m.from_name} · ` : ''}{m.from_email ?? '—'}{m.ak ? <> · <b>Akte:</b> {m.ak}</> : null}{m.betrag_text ? <> · <b>Betrag:</b> {m.betrag_text}</> : null}{m.has_attachments ? ' · 📎 Anhang' : ''}{m.stellungnahme ? ' · ⚖ Stellungnahme→Gutachten' : ''}{m.nachbesichtigung ? ' · 👁 Nachbesichtigung→Gutachten' : ''}</div>
                  <div className="bg-white border rounded-md p-3 whitespace-pre-wrap">{m.snippet ?? '(keine Vorschau)'}</div>
                </div>
              )}
            </div>
          ))}
          {!gef.length && <div className="px-3 py-10 text-center text-sm text-muted-foreground">Keine Mails.</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Versicherungs-Statistik (Lernen) + Kürzungs-Auswertung ─────────────────
function VersicherungsStatistik() {
  const [rows, setRows] = useState<Fall[]>([]);
  const [kuerz, setKuerz] = useState<Fall[]>([]);
  const [loading, setLoading] = useState(true);
  const [suche, setSuche] = useState('');

  useEffect(() => {
    let abbruch = false;
    (async () => {
      const r = await supabase.from('v_forderungs_versicherungs_statistik').select('*').order('offen_gesamt', { ascending: false }).limit(2000);
      const k = await supabase.from('v_forderungs_kuerzungen').select('*').limit(2000);
      if (!abbruch) { setRows(r.error ? [] : (r.data ?? [])); setKuerz(k.error ? [] : (k.data ?? [])); setLoading(false); }
    })();
    return () => { abbruch = true; };
  }, []);

  const gef = useMemo(() => {
    if (!suche.trim()) return rows;
    const q = suche.toLowerCase();
    return rows.filter((x) => String(x.versicherung || '').toLowerCase().includes(q));
  }, [rows, suche]);

  const gefKuerz = useMemo(() => {
    if (!suche.trim()) return kuerz;
    const q = suche.toLowerCase();
    return kuerz.filter((x) => [x.versicherung, x.ak, x.subject].filter(Boolean).some((v: any) => String(v).toLowerCase().includes(q)));
  }, [kuerz, suche]);

  if (loading) return <div className="text-sm text-muted-foreground p-6">Lade Versicherungs-Statistik…</div>;
  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" /><input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Versicherung / AK / Betreff suchen" className="pl-7 pr-2 py-1.5 text-sm border rounded-md w-72 bg-white" /></div>
          <span className="text-xs text-muted-foreground ml-auto">{gef.length} Versicherungen · so verhält sich wer</span>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <div className="max-h-[48vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b text-xs text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Versicherung</th>
                  <th className="text-right font-medium px-3 py-2">Fälle</th>
                  <th className="text-right font-medium px-3 py-2">Offen</th>
                  <th className="text-right font-medium px-3 py-2">Kürzung/Teilz.</th>
                  <th className="text-right font-medium px-3 py-2">Mögl. bezahlt</th>
                  <th className="text-right font-medium px-3 py-2">Verjährt</th>
                  <th className="text-right font-medium px-3 py-2">Mails: Kürzung/Zahlung/Stellungn.</th>
                </tr>
              </thead>
              <tbody>
                {gef.map((r, i) => (
                  <tr key={i} className="border-b hover:bg-muted/30">
                    <td className="px-3 py-2 font-medium truncate max-w-[280px]">{r.versicherung}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.faelle}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{eur(r.offen_gesamt)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">{r.kuerzung_teilzahlung}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{r.vermutlich_bezahlt}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-600">{r.verjaehrt}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{r.mails_kuerzung} / {r.mails_zahlung} / {r.mails_stellungnahme}</td>
                  </tr>
                ))}
                {!gef.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-muted-foreground">Keine Daten.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">Lern-Übersicht je Gesellschaft (wie oft gekürzt, gezahlt, Stellungnahme angefragt). Wächst mit jeder ausgewerteten Eingangsmail.</p>
      </div>

      <div>
        <div className="flex items-center gap-1.5 text-sm font-medium mb-2"><Scissors className="w-4 h-4 text-amber-600" /> Erkannte Kürzungen & Rückforderungen <span className="text-xs text-muted-foreground font-normal">({gefKuerz.length} aus der Eingangspost gelesen)</span></div>
        <div className="border rounded-lg overflow-hidden">
          <div className="max-h-[42vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b text-xs text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Datum</th>
                  <th className="text-left font-medium px-3 py-2">AK</th>
                  <th className="text-left font-medium px-3 py-2">Versicherung</th>
                  <th className="text-right font-medium px-3 py-2">Offen</th>
                  <th className="text-left font-medium px-3 py-2">Betrag (Schreiben)</th>
                  <th className="text-left font-medium px-3 py-2">Betreff</th>
                </tr>
              </thead>
              <tbody>
                {gefKuerz.map((r, i) => (
                  <tr key={i} className="border-b hover:bg-muted/30">
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{datum(r.datum)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.ak ?? '—'}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[200px]">{r.versicherung ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{eur(r.offen)}</td>
                    <td className="px-3 py-2 text-xs font-semibold text-amber-800 whitespace-nowrap">{r.betrag ?? '—'}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[260px]" title={r.subject ?? ''}>{r.subject ?? '—'}</td>
                  </tr>
                ))}
                {!gefKuerz.length && <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-muted-foreground">Noch keine Kürzungen erkannt — wird durch das Anhang-Auslesen laufend befüllt.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">Aus den per OCR gelesenen Abrechnungs-/Kürzungsschreiben extrahiert. Der Betrag ist der im Schreiben erkannte Wert — füllt sich, während die Anhänge abgearbeitet werden.</p>
      </div>
    </div>
  );
}

// ─── Versandplaner (10-Minuten-Wochenplan, scrollbar) ───────────────────────
function nextBusiness8(from: Date): Date {
  for (let k = 0; k < 14; k++) {
    const x = new Date(from); x.setDate(from.getDate() + k);
    const dow = x.getDay(); if (dow === 0 || dow === 6) continue;
    const open = new Date(x); open.setHours(8, 0, 0, 0);
    const close = new Date(x); close.setHours(18, 0, 0, 0);
    if (from <= open) return open;
    if (from < close) return new Date(from);
  }
  const r = new Date(from); r.setDate(from.getDate() + 1); r.setHours(8, 0, 0, 0); return r;
}
function advance(t: Date, stepMin: number): Date {
  let x = new Date(t.getTime() + stepMin * 60000);
  for (let i = 0; i < 30; i++) {
    const dow = x.getDay();
    const out = x.getHours() >= 18 || x.getHours() < 8 || dow === 0 || dow === 6;
    if (!out) break;
    const n = new Date(x);
    if (x.getHours() >= 18) n.setDate(x.getDate() + 1);
    n.setHours(8, 0, 0, 0);
    while (n.getDay() === 0 || n.getDay() === 6) n.setDate(n.getDate() + 1);
    x = n;
  }
  return x;
}
function schedule(items: Fall[], perSlot: number, stepMin: number, start: Date) {
  let t = new Date(start); let inSlot = 0;
  for (const it of items) { if (inSlot >= perSlot) { t = advance(t, stepMin); inSlot = 0; } it.when = new Date(t); inSlot++; }
}

function VersandPlaner() {
  const [rows, setRows] = useState<Fall[]>([]);
  const [pipe, setPipe] = useState<Fall | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'alle' | 'mahn' | 'verj'>('alle');
  const [weekOffset, setWeekOffset] = useState(0);
  const [tip, setTip] = useState<{ x: number; y: number; html: any } | null>(null);

  useEffect(() => {
    let abbruch = false;
    (async () => {
      const { data } = await supabase.from('v_forderungs_versandplan').select('*').limit(5000);
      const p = await supabase.from('v_forderungs_pipeline').select('*').limit(1);
      if (!abbruch) { setRows(data ?? []); setPipe(p.error ? null : ((p.data ?? [])[0] ?? null)); setLoading(false); }
    })();
    return () => { abbruch = true; };
  }, []);

  const { all, baseMonday, stats } = useMemo(() => {
    const now = new Date(); const start = nextBusiness8(now);
    const sent = rows.filter((d) => d.status === 'sent' && d.sent_at).map((d) => ({ ...d, when: new Date(d.sent_at), done: true }));
    const mahn = rows.filter((d) => d.art === 'forderungs-eskalation' && d.status === 'ready_for_review').sort((a, b) => (b.stufe - a.stufe) || (b.betrag - a.betrag));
    const verj = rows.filter((d) => d.art === 'verjaehrt-anfrage' && d.status === 'ready_for_review').sort((a, b) => b.betrag - a.betrag);
    schedule(mahn, STAPEL, 10, start); schedule(verj, 5, 30, start);
    const all = sent.concat(mahn, verj);
    const future = all.filter((x) => !x.done).map((x) => x.when).sort((a, b) => +a - +b);
    const ref = future.length ? future[0] : now;
    const bm = new Date(ref); const dow = (bm.getDay() + 6) % 7; bm.setDate(bm.getDate() - dow); bm.setHours(0, 0, 0, 0);
    const held = rows.filter((d) => d.status === 'draft').length;
    const sumQueued = mahn.concat(verj).reduce((s, x) => s + (+x.betrag || 0), 0);
    const last = future.length ? future[future.length - 1] : null;
    return { all, baseMonday: bm, stats: { mahn: mahn.length, verj: verj.length, held, sumQueued, sent: sent.length, last } };
  }, [rows]);

  const items = useMemo(() => {
    if (filter === 'mahn') return all.filter((x) => x.art === 'forderungs-eskalation');
    if (filter === 'verj') return all.filter((x) => x.art === 'verjaehrt-anfrage');
    return all;
  }, [all, filter]);

  if (loading) return <div className="text-sm text-muted-foreground p-6">Lade Versandplan…</div>;

  const mon = new Date(baseMonday); mon.setDate(mon.getDate() + weekOffset * 7);
  const days = [...Array(5)].map((_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d; });
  const slots: { h: number; m: number }[] = [];
  for (let h = 8; h < 18; h++) for (const m of [0, 10, 20, 30, 40, 50]) slots.push({ h, m });

  return (
    <div className="space-y-3" onMouseLeave={() => setTip(null)}>
      {pipe && (
        <div className="border rounded-lg p-3 bg-white">
          <div className="text-xs font-medium text-muted-foreground mb-2">Gesamt-Pipeline (alle Mahnungen)</div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{pipe.freigegeben} freigegeben</span>
            <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold">{pipe.zurueckgehalten} zurückgehalten</span>
            <span className="text-muted-foreground">davon:</span>
            <span className="px-2 py-1 rounded-full bg-zinc-100 text-zinc-700">{pipe.grund_unterlagen} Unterlagen fehlen</span>
            <span className="px-2 py-1 rounded-full bg-zinc-100 text-zinc-700">{pipe.grund_alt} Bank-Historie &lt; 05/2024</span>
            <span className="px-2 py-1 rounded-full bg-zinc-100 text-zinc-700">{pipe.grund_bezahlt} schon bezahlt</span>
            <span className="px-2 py-1 rounded-full bg-zinc-100 text-zinc-700">{pipe.grund_unsicher} verjährt/bezahlt</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Der Plan zeigt nur die <b>freigegebenen</b> Schreiben. Zurückgehaltene erscheinen automatisch, sobald Unterlagen (OCR/Reklassifizierung) bzw. die Bank-Historie vorliegen; bereits bezahlte bleiben dauerhaft draußen. Deshalb sind Folgetage noch leer.</p>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {[['Mahnungen frei', stats.mahn], ['Verjährt-Anfragen', stats.verj], ['Zurückgehalten', stats.held], ['Σ freigegeben', eur(stats.sumQueued)], ['Versendet', stats.sent], ['Vsl. fertig', stats.last ? stats.last.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '—']].map((k, i) => (
          <div key={i} className="border rounded-lg p-2 bg-white"><div className="text-sm font-semibold">{k[1]}</div><div className="text-[11px] text-muted-foreground">{k[0]}</div></div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {(['alle', 'mahn', 'verj'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`text-xs border rounded-md px-2.5 py-1 ${filter === f ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white text-muted-foreground'}`}>
            {f === 'alle' ? 'Alle' : f === 'mahn' ? 'Mahnungen' : 'Verjährung'}
          </button>
        ))}
        <span className="mx-1" />
        <button onClick={() => setWeekOffset((w) => w - 1)} className="text-xs border rounded-md px-2 py-1 bg-white">◀</button>
        <span className="text-sm font-semibold min-w-[150px] text-center">KW ab {mon.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</span>
        <button onClick={() => setWeekOffset((w) => w + 1)} className="text-xs border rounded-md px-2 py-1 bg-white">▶</button>
        <div className="flex gap-2 flex-wrap ml-auto">
          {[0, 1, 2, 3, 4, 5, 6, 9].map((s) => (<span key={s} className="flex items-center gap-1 text-[10px] text-muted-foreground"><span className="w-3 h-3 rounded" style={{ background: STUFE_FARBE[s] }} />{s === 9 ? 'Verj' : 'S' + s}</span>))}
        </div>
      </div>

      <div className="border rounded-xl bg-white overflow-auto" style={{ maxHeight: '68vh' }}>
        <div className="grid gap-1 p-3" style={{ gridTemplateColumns: '60px repeat(5,minmax(150px,1fr))' }}>
          <div className="sticky top-0 bg-white z-10" />
          {days.map((d, i) => (<div key={i} className="sticky top-0 bg-white z-10 text-center text-xs font-semibold text-gray-600 pb-1">{d.toLocaleDateString('de-DE', { weekday: 'short' })}<div className="text-[10px] text-gray-400 font-normal">{d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</div></div>))}
          {slots.map((slot) => (
            <SlotRow key={slot.h * 60 + slot.m} slot={slot} days={days} items={items} onTip={setTip} />
          ))}
        </div>
      </div>

      {tip && (
        <div className="fixed z-50 max-w-[330px] bg-gray-900 text-gray-50 rounded-lg p-3 text-xs shadow-xl pointer-events-none" style={{ left: tip.x, top: tip.y }}>
          {tip.html}
        </div>
      )}
      <div className="text-xs text-muted-foreground">Jede Zeile = ein 10-Minuten-Sendetakt (Mahnungen {STAPEL}/Takt, max. 60/Tag, je Empfänger 1/Tag, Mo–Fr 08:00–17:50). Scrollen für den ganzen Tag. Zurückgehaltene erscheinen erst nach Freigabe.</div>
    </div>
  );
}

function SlotRow({ slot, days, items, onTip }: { slot: { h: number; m: number }; days: Date[]; items: Fall[]; onTip: (t: any) => void }) {
  const { h, m } = slot;
  return (
    <>
      <div className="text-[11px] text-gray-400 text-right pr-1 flex items-start justify-end pt-1">{pad(h)}:{pad(m)}</div>
      {days.map((d, i) => {
        const cell = items.filter((x) => x.when && x.when.getFullYear() === d.getFullYear() && x.when.getMonth() === d.getMonth() && x.when.getDate() === d.getDate() && x.when.getHours() === h && Math.floor(x.when.getMinutes() / 10) * 10 === m);
        if (!cell.length) return <div key={i} className="min-h-[26px] border border-gray-50 rounded opacity-40" />;
        const byStage: Record<string, number> = {}; let sum = 0;
        cell.forEach((x) => { const s = (x.stufe in STUFE_FARBE) ? x.stufe : 0; byStage[s] = (byStage[s] || 0) + 1; sum += +x.betrag || 0; });
        const done = cell[0].done;
        const tipHtml = (
          <div>
            <b>{done ? '✅ Versendet' : '⏱ Geplant'} · {d.toLocaleDateString('de-DE')} {pad(h)}:{pad(m)}</b>
            <div>{cell.length} Mail(s) · Σ {eur(sum)}</div>
            {cell.slice(0, 12).map((it, k) => (
              <div key={k} className="flex justify-between gap-2 border-t border-gray-700 pt-1 mt-1">
                <span><span className="px-1.5 rounded text-[10px] font-semibold" style={{ background: STUFE_FARBE[(it.stufe in STUFE_FARBE) ? it.stufe : 0], color: '#111' }}>{it.stufe === 9 ? 'Verj' : 'S' + it.stufe}</span> {it.geschaedigter || '(Akte)'}<br /><span className="text-gray-400">{it.versicherung || ''}</span></span>
                <span>{eur(+it.betrag || 0)}</span>
              </div>
            ))}
            {cell.length > 12 && <div className="text-gray-400 mt-1">… und {cell.length - 12} weitere</div>}
          </div>
        );
        return (
          <div key={i} className="min-h-[26px] border border-gray-100 rounded p-0.5 flex flex-col gap-0.5 hover:outline hover:outline-2 hover:outline-amber-400"
            onMouseMove={(e) => onTip({ x: Math.min(e.clientX + 14, window.innerWidth - 340), y: Math.min(e.clientY + 14, window.innerHeight - 240), html: tipHtml })}
            onMouseLeave={() => onTip(null)}>
            {Object.keys(byStage).sort((a, b) => +b - +a).map((s) => (
              <div key={s} className="text-[9px] rounded px-1 truncate" style={{ background: STUFE_FARBE[+s] }}>{byStage[s]}× {s === '9' ? 'Verj' : 'S' + s}</div>
            ))}
            <div className="text-[8px] text-gray-500">{eur(sum)}</div>
          </div>
        );
      })}
    </>
  );
}

// ─── Fall-Detail ────────────────────────────────────────────────────────────
function FallDetail({ fall, onClose }: { fall: Fall; onClose: () => void }) {
  const ak = fall.ak ?? fall.akte_id; const akteId = fall.akte_id ?? null;
  const [ausgang, setAusgang] = useState<Fall[]>([]);
  const [eingang, setEingang] = useState<Fall[]>([]);
  const [ladenMails, setLadenMails] = useState(true);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusInfo, setStatusInfo] = useState<string | null>(null);
  const [abgleich, setAbgleich] = useState<Fall | null>(null);
  const [notizen, setNotizen] = useState<Fall[]>([]);
  const [neueNotiz, setNeueNotiz] = useState('');
  const [notizBusy, setNotizBusy] = useState(false);

  const ladeNotizen = useCallback(async () => {
    if (!ak) return;
    const r = await supabase.from('forderung_notiz').select('*').eq('ak', ak).order('erstellt_am', { ascending: false });
    if (!r.error) setNotizen(r.data ?? []);
  }, [ak]);

  useEffect(() => {
    let abbruch = false; setLadenMails(true); setStatusInfo(null);
    (async () => {
      let aus: Fall[] = [];
      if (akteId) { const r = await supabase.from('email_drafts').select('subject,recipient,status,sent_at,created_at').eq('akte_id', akteId).order('created_at', { ascending: false }); if (!r.error) aus = r.data ?? []; }
      let ein: Fall[] = [];
      if (akteId) { const r = await supabase.from('eingang_anwaltspost').select('subject,from_email,datum,kategorie_fein').eq('akte_id', akteId).order('datum', { ascending: false }); if (!r.error) ein = r.data ?? []; }
      const ab = await supabase.from('v_forderungs_abgleich').select('*').eq('ak', ak).limit(1);
      if (!abbruch) { setAusgang(aus); setEingang(ein); setAbgleich(ab.error ? null : ((ab.data ?? [])[0] ?? null)); setLadenMails(false); }
      ladeNotizen();
    })();
    return () => { abbruch = true; };
  }, [akteId, ak, ladeNotizen]);

  const setzeStatus = async (status: string, label: string) => {
    if (!ak) return; setStatusBusy(true); setStatusInfo(null);
    const r = await supabase.from('forderungs_eskalation_log').update({ status }).eq('ak', ak);
    setStatusBusy(false); setStatusInfo(r.error ? `Fehler: ${r.error.message}` : `Status gesetzt: ${label}`);
  };

  const speichereNotiz = async (art: string) => {
    if (!ak || !neueNotiz.trim()) return; setNotizBusy(true);
    const r = await supabase.from('forderung_notiz').insert({ ak, akte_id: akteId, art, text: neueNotiz.trim(), status: 'offen' });
    setNotizBusy(false);
    if (!r.error) { setNeueNotiz(''); ladeNotizen(); }
  };

  const abwLabel: Record<string, string> = {
    bank_deckt_offen: 'Bank deckt Offenbetrag – evtl. schon bezahlt',
    winvalue_vs_buchhaltung: 'WinValue weicht von Buchhaltung ab',
    bank_vs_buchhaltung: 'Bankzahlung weicht von gebuchter Zahlung ab',
    keine_buchhaltungsrechnung: 'Keine Buchhaltungsrechnung gefunden (WinValue-Altfall)',
  };

  return (
    <div className="w-[420px] border rounded-lg bg-white flex-shrink-0 max-h-[78vh] overflow-y-auto">
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b sticky top-0 bg-white">
        <div><div className="font-mono text-sm font-bold">{ak}</div><div className="text-xs text-muted-foreground">{fall.geschaedigter ?? '—'}{fall.gegner_name ? ` ./. ${fall.gegner_name}` : ''}</div></div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm">✕</button>
      </div>
      <div className="p-4 space-y-4 text-sm">
        {abgleich && abgleich.abweichung && abgleich.abweichung !== 'ok' && (
          <div className={`flex items-start gap-2 text-xs rounded-md border px-2.5 py-2 ${abgleich.abweichung === 'keine_buchhaltungsrechnung' ? 'bg-zinc-50 border-zinc-200 text-zinc-600' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <div><b>Abgleich:</b> {abwLabel[abgleich.abweichung] ?? abgleich.abweichung}<br />
              <span className="text-[11px]">WinValue offen {eur(abgleich.wv_offen)} · Bank gezahlt {eur(abgleich.bank_gezahlt)}{abgleich.rb_offen != null ? ` · Buchhaltung offen ${eur(abgleich.rb_offen)}` : ''}</span>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <Zeile label="Offen" wert={eur(fall.offen)} bold />
          <Zeile label="Verzugszins" wert={eur(fall.verzugszins_eur)} />
          <Zeile label="Auftrag" wert={datum(fall.auftragsdatum)} />
          <Zeile label="Rechnung" wert={datum(fall.rechnung_datum)} />
          <Zeile label="Schaden vom" wert={datum(fall.schadentag)} />
          <Zeile label="Verjährung" wert={String(fall.verjaehrung_jahr ?? '—')} />
          <Zeile label="Schaden-Nr." wert={fall.schadennummer ?? '—'} />
          <Zeile label="Vers.-Nr." wert={fall.versicherungsnummer ?? '—'} />
          <Zeile label="Versicherung" wert={fall.versicherung ?? '—'} />
          <Zeile label="Kanzlei" wert={fall.kanzlei ?? '—'} />
          <Zeile label="Unterlagen" wert={fall.unterlagen_vollstaendig ? 'vollständig' : 'unvollständig'} />
          <Zeile label="letzte Zahlung" wert={datum(fall.bank_letzte_zahlung)} />
        </div>
        <div>
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Nächster Status</div>
          <div className="flex flex-wrap gap-1.5">
            <StatusBtn label="Antwort erfasst" onClick={() => setzeStatus('antwort_pruefen', 'Antwort erfasst')} busy={statusBusy} />
            <StatusBtn label="An Kanzlei" onClick={() => setzeStatus('an_kanzlei', 'an Kanzlei übergeben')} busy={statusBusy} />
            <StatusBtn label="Erledigt" onClick={() => setzeStatus('erledigt', 'erledigt')} busy={statusBusy} />
          </div>
          {statusInfo && (<div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" />{statusInfo}</div>)}
        </div>

        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5"><MessageSquare className="w-3 h-3" /> Notizen / Rückfragen</div>
          <textarea value={neueNotiz} onChange={(e) => setNeueNotiz(e.target.value)} rows={2} placeholder="Info, Rückfrage oder Hinweis zu diesem Fall…" className="w-full border rounded-md px-2 py-1.5 text-xs" />
          <div className="flex gap-1.5 mt-1">
            <button type="button" onClick={() => speichereNotiz('kommentar')} disabled={notizBusy || !neueNotiz.trim()} className="text-xs border rounded-md px-2 py-1 bg-white hover:bg-muted/40 disabled:opacity-50">Notiz speichern</button>
            <button type="button" onClick={() => speichereNotiz('rueckfrage')} disabled={notizBusy || !neueNotiz.trim()} className="text-xs border rounded-md px-2 py-1 bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-50">Als Rückfrage markieren</button>
          </div>
          {notizen.length > 0 && (
            <ul className="space-y-1.5 mt-2">
              {notizen.map((n) => (
                <li key={n.id} className="text-xs border rounded-md px-2 py-1.5">
                  <div className="flex items-center gap-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded-full ${n.art === 'bounce' ? 'bg-red-100 text-red-700' : n.art === 'rueckfrage' ? 'bg-amber-100 text-amber-800' : n.art === 'stellungnahme' ? 'bg-violet-100 text-violet-700' : n.art === 'nachbesichtigung' ? 'bg-sky-100 text-sky-700' : 'bg-zinc-100 text-zinc-600'}`}>{n.art}</span><span className="text-[10px] text-muted-foreground">{datumZeit(n.erstellt_am)}</span></div>
                  <div className="mt-0.5">{n.text}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5"><Send className="w-3 h-3" /> Ausgehend</div>
          {ladenMails ? <div className="text-xs text-muted-foreground">Lade…</div> : ausgang.length === 0 ? <div className="text-xs text-muted-foreground">Noch keine ausgehenden Mails.</div> : (
            <ul className="space-y-1.5">{ausgang.map((m, i) => (<li key={i} className="text-xs border rounded-md px-2 py-1.5"><div className="font-medium truncate">{m.subject ?? '—'}</div><div className="text-muted-foreground truncate">{m.recipient ?? '—'}</div><div className="text-[10px] text-muted-foreground">{m.status} · {datum(m.sent_at ?? m.created_at)}</div></li>))}</ul>
          )}
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5"><Inbox className="w-3 h-3" /> Eingehend (Anwaltspost)</div>
          {ladenMails ? <div className="text-xs text-muted-foreground">Lade…</div> : eingang.length === 0 ? <div className="text-xs text-muted-foreground">Keine eingehende Post zugeordnet.</div> : (
            <ul className="space-y-1.5">{eingang.map((m, i) => (<li key={i} className="text-xs border rounded-md px-2 py-1.5"><div className="font-medium truncate">{m.subject ?? '—'}</div><div className="text-muted-foreground truncate">{m.from_email ?? '—'}</div><div className="text-[10px] text-muted-foreground">{m.kategorie_fein ?? ''} · {datum(m.datum)}</div></li>))}</ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBtn({ label, onClick, busy }: { label: string; onClick: () => void; busy: boolean }) {
  return (<button type="button" onClick={onClick} disabled={busy} className="text-xs border rounded-md px-2 py-1 bg-white hover:bg-muted/40 disabled:opacity-50">{label}</button>);
}
function Zeile({ label, wert, bold }: { label: string; wert: string; bold?: boolean }) {
  return (<><span className="text-muted-foreground">{label}:</span><span className={bold ? 'font-semibold tabular-nums' : 'tabular-nums'}>{wert}</span></>);
}

// ─── Vorlagen-Editor mit Live-Vorschau ──────────────────────────────────────
const VORSCHAU_BEISPIEL: Record<string, string> = {
  ak: 'AK2509154', geschaedigter: 'Max Mustermann', vn: 'Erika Beispiel (Schädiger)',
  versicherung: 'Musterversicherung AG', schadennummer: 'SCH-2026-12345', versicherungsnummer: 'VS-67890',
  schadentag: '17.03.2026', rechnungsdatum: '02.04.2026', auftragsdatum: '18.03.2026',
  faellig: '16.04.2026', pruef_ende: '14.05.2026', verzug_seit: '14.05.2026', verzugsbeginn: '14.05.2026',
  offen: '452,20 €', brutto: '1.200,00 €', verzugszins: '12,34 €', gesamt: '464,54 €',
  basiszins: '1,27 %', verzugssatz: '6,27 %', zahldatum: '—', kurzfrist: '07.07.2026',
  kanzlei: 'Rechtsanwälte Muster & Partner',
  cc_block: '<br><b>CC (Rechtsanwalt):</b> Rechtsanwälte Muster & Partner',
  dringlichkeit: 'höflich darauf hinweisen, dass unser Sachverständigenhonorar zur Zahlung fällig ist',
  verzugszins_block: '<b>Verzugszinsen.</b> Nach Ablauf der Prüffrist am <b>14.05.2026</b> ist Verzug eingetreten (§ 286 BGB); seither 6,27 % p. a. (5 Prozentpunkte über Basiszins 1,27 %, § 288 Abs. 1 i. V. m. § 247 BGB).',
  kanzlei_block: '<b>An die bevollmächtigte Kanzlei (in Kopie):</b> Sie sind in Kopie dieser Nachricht.',
  stufen_hinweis: 'Wir bitten um Ausgleich bis zum 07.07.2026.',
};
function fuelleVorlage(html: string): string {
  return (html || '').replace(/\{\{(\w+)\}\}/g, (_m, k) => VORSCHAU_BEISPIEL[k] ?? `[${k}]`);
}

function VorlagenEditor() {
  const [vorlagen, setVorlagen] = useState<Fall[]>([]);
  const [aktiv, setAktiv] = useState<number | null>(null);
  const [betreff, setBetreff] = useState('');
  const [koerper, setKoerper] = useState('');
  const [laden, setLaden] = useState(true);
  const [speichern, setSpeichern] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    let abbruch = false;
    (async () => {
      const r = await supabase.from('forderungs_vorlagen').select('*').order('stufe');
      if (abbruch) return;
      if (!r.error) { const liste = r.data ?? []; setVorlagen(liste); if (liste.length) { setAktiv(liste[0].stufe); setBetreff(liste[0].betreff ?? ''); setKoerper(liste[0].koerper ?? ''); } }
      setLaden(false);
    })();
    return () => { abbruch = true; };
  }, []);

  const waehle = (v: Fall) => { setAktiv(v.stufe); setBetreff(v.betreff ?? ''); setKoerper(v.koerper ?? ''); setInfo(null); };
  const speichereVorlage = async () => {
    if (aktiv === null) return; setSpeichern(true); setInfo(null);
    const r = await supabase.from('forderungs_vorlagen').update({ betreff, koerper, aktualisiert_am: new Date().toISOString() }).eq('stufe', aktiv);
    setSpeichern(false); setInfo(r.error ? `Fehler: ${r.error.message}` : 'Vorlage gespeichert.');
    if (!r.error) setVorlagen((prev) => prev.map((v) => (v.stufe === aktiv ? { ...v, betreff, koerper } : v)));
  };

  if (laden) return <div className="text-sm text-muted-foreground">Lade Vorlagen…</div>;
  return (
    <div className="flex gap-4 items-start">
      <div className="w-56 border rounded-lg overflow-hidden flex-shrink-0">
        <div className="px-3 py-2 text-xs text-muted-foreground border-b bg-muted/30">Vorlagen je Stufe</div>
        <ul>{vorlagen.map((v) => (<li key={v.stufe}><button type="button" onClick={() => waehle(v)} className={`w-full text-left px-3 py-2 text-sm border-b hover:bg-muted/40 ${aktiv === v.stufe ? 'bg-amber-50' : ''}`}><div className="font-medium">Stufe {v.stufe}: {STUFE_LABEL[v.stufe] ?? v.bezeichnung ?? ''}</div><div className="text-xs text-muted-foreground truncate">{v.bezeichnung ?? ''}</div></button></li>))}</ul>
      </div>
      <div className="flex-1 min-w-0 border rounded-lg p-4 space-y-3">
        {aktiv === null ? <div className="text-sm text-muted-foreground">Vorlage links auswählen.</div> : (
          <>
            <div><label className="text-xs text-muted-foreground">Betreff</label><input value={betreff} onChange={(e) => setBetreff(e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm mt-1" /></div>
            <div><label className="text-xs text-muted-foreground">Körper (HTML, Platzhalter wie {'{{geschaedigter}}'} möglich)</label><textarea value={koerper} onChange={(e) => setKoerper(e.target.value)} rows={20} className="w-full border rounded-md px-2 py-1.5 text-xs font-mono mt-1" /></div>
            <div className="flex items-center gap-2"><button type="button" onClick={speichereVorlage} disabled={speichern} className="flex items-center gap-1.5 text-sm rounded-md px-3 py-1.5 bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">{speichern ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Speichern</button>{info && <span className="text-xs text-muted-foreground">{info}</span>}</div>
          </>
        )}
      </div>
      <div className="w-[460px] flex-shrink-0">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1.5"><Eye className="w-3.5 h-3.5" /> Live-Vorschau (mit Beispieldaten)</div>
        <div className="border rounded-lg overflow-hidden bg-[#f3f4f6] max-h-[72vh] overflow-y-auto">
          <div style={{ background: '#7f1d1d', color: '#fff', padding: '14px 18px' }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.2 }}>Kfz-Sachverständigenbüro Koop</div>
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>Forderungsmanagement · forderungen@gutachter-koop.de</div>
          </div>
          <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 18px', fontSize: 12 }}>
            <span style={{ color: '#6b7280' }}>Betreff: </span><span style={{ fontWeight: 600 }}>{fuelleVorlage(betreff)}</span>
          </div>
          <div style={{ background: '#fff', padding: '16px 18px', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13.5, color: '#1a1d21', lineHeight: 1.55 }}
            dangerouslySetInnerHTML={{ __html: fuelleVorlage(koerper) }} />
          <div style={{ background: '#fafafa', borderTop: '1px solid #e5e7eb', padding: '12px 18px', fontSize: 10.5, color: '#6b7280', lineHeight: 1.5 }}>
            Kfz-Sachverständigenbüro Koop · Antwort bitte ausschließlich an forderungen@gutachter-koop.de<br />
            IBAN DE11 4505 0001 0001 3181 95 · BIC WELADE3HXXX
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">Vorschau mit Beispieldaten – im Versand werden die echten Falldaten eingesetzt. Anhänge (Honorarvereinbarung + Rechnung) werden automatisch beigefügt.</p>
      </div>
    </div>
  );
}
